import type { NetChannel, NetMessage, PeerId, SessionRole } from './transport';
import { WebRTCTransport } from './webrtcTransport';
import { checkProvenance } from './messageProvenance';
import {
  ClockSyncClient,
  makeClockPong,
  type ClockPong,
  type ClockPing,
} from './clockSync';

export class NetSession {
  public role: SessionRole = 'host';
  public phase: 'hub' | 'dungeon' = 'hub';
  private transport: WebRTCTransport;
  private clock: ClockSyncClient | null = null;
  private appCbs = new Set<(from: PeerId, msg: NetMessage) => void>();
  private typedCbs = new Map<
    string,
    Set<(from: PeerId, msg: NetMessage) => void>
  >();

  constructor(private now: () => number = () => performance.now()) {
    this.transport = new WebRTCTransport();
    this.transport.onMessage((from, msg) => this.route(from, msg));
  }

  getPhase(): 'hub' | 'dungeon' {
    return this.phase;
  }
  setPhase(p: 'hub' | 'dungeon'): void {
    this.phase = p;
  }

  async host(code: string, roomId: string): Promise<void> {
    this.role = 'host';
    await this.transport.host(code, roomId);
  }
  /** Stable room identity (UUID): set at create (host) / from `joined` (client).
   *  Empty until the session connects. */
  roomId(): string {
    return this.transport.roomId;
  }
  /** One-shot signaling notice that this client left run `code` (token-tagged)
   *  while the host was unreachable — relayed to the host when it re-hosts. */
  leaveRun(code: string, token: string): Promise<void> {
    return this.transport.leaveRun(code, token);
  }
  /** Host: tokens of clients that left while we were down, delivered by the
   *  signaling server when we re-create the lobby. */
  onDepartures(cb: (tokens: string[]) => void): () => void {
    return this.transport.onDepartures(cb);
  }
  /** Host (on deliberate close): mark run `code` ended so a later reconnect from
   *  a dropped client is told the run is over rather than waiting for a re-host. */
  endRun(code: string): void {
    this.transport.endRun(code);
  }
  /** Client: signaling reports that the run we tried to rejoin was deliberately
   *  ended (host left), as opposed to a host that may still re-host. */
  onRunClosed(cb: () => void): () => void {
    return this.transport.onRunClosed(cb);
  }
  /** Signaling-level error (e.g. a join rejected because the lobby doesn't
   *  exist). Lets the join flow fail loudly instead of waiting in vain. */
  onError(cb: (message: string) => void): () => void {
    return this.transport.onError(cb);
  }
  /** Host: signaling confirmed the lobby was created (vs rejecting the code as
   *  already in use). Lets the host flow wait for the ack instead of assuming. */
  onCreated(cb: (peerId: PeerId) => void): () => void {
    return this.transport.onCreated(cb);
  }
  async join(code: string): Promise<void> {
    this.role = 'client';
    await this.transport.joinLobby(code);
    this.clock = new ClockSyncClient(
      (msg) => this.transport.broadcast('reliable', msg),
      this.now,
    );
    this.transport.onPeerJoin(() => this.clock?.start());
  }

  private route(from: PeerId, msg: NetMessage): void {
    if (msg.type === 'clock-ping' && this.role === 'host') {
      this.transport.send(
        from,
        'reliable',
        makeClockPong(msg as ClockPing, this.now()),
      );
      return;
    }
    if (msg.type === 'clock-pong' && this.clock) {
      this.clock.onPong(msg as ClockPong);
      return;
    }
    const verdict = checkProvenance(msg, from, this.hostPeerId());
    if (!verdict.ok) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[net] dropped ${msg.type} from ${from} (provenance)`);
      }
      return;
    }
    if (import.meta.env.DEV && verdict.undeclared) {
      // eslint-disable-next-line no-console
      console.warn(
        `[net] ${msg.type} has no declared provenance (defaulted host-only)`,
      );
    }
    this.appCbs.forEach((cb) => cb(from, msg));
    this.typedCbs.get(msg.type)?.forEach((cb) => cb(from, msg));
  }

  send(peerId: PeerId, channel: NetChannel, msg: NetMessage): void {
    this.transport.send(peerId, channel, msg);
  }
  broadcast(channel: NetChannel, msg: NetMessage): void {
    this.transport.broadcast(channel, msg);
  }
  onMessage(cb: (from: PeerId, msg: NetMessage) => void): () => void {
    this.appCbs.add(cb);
    return () => this.appCbs.delete(cb);
  }
  on(
    type: string,
    handler: (from: PeerId, msg: NetMessage) => void,
  ): () => void {
    let set = this.typedCbs.get(type);
    if (!set) {
      set = new Set();
      this.typedCbs.set(type, set);
    }
    set.add(handler);
    return () => {
      this.typedCbs.get(type)?.delete(handler);
    };
  }
  onPeerJoin(cb: (peerId: PeerId) => void): () => void {
    return this.transport.onPeerJoin(cb);
  }
  clockOffset(): number {
    return this.clock?.getOffset() ?? 0;
  }
  /** Estimated host clock — used by the client to time snapshot interpolation. */
  clientHostNow(): number {
    return this.clock?.hostNow() ?? this.now();
  }
  peers(): PeerId[] {
    return this.transport.peers();
  }
  localPeerId(): PeerId {
    return this.transport.localPeerId;
  }
  hostPeerId(): PeerId {
    return this.transport.hostPeerId;
  }
  onPeerLeave(cb: (peerId: PeerId) => void): () => void {
    return this.transport.onPeerLeave(cb);
  }
  close(): void {
    this.clock?.stop();
    this.transport.close();
  }
}
